import { lazy } from 'react'
import { Lazy, isLazy, isSuspense } from 'react-is'
import libs from '../../libs/lazy-ui'
import { mergeExposes } from '../../init/init'
import { importLibs } from '../../libs/base'
import {
  inited,
  events,
  onAuthed,
  getFrameworkVar,
  getFrameworkVars,
  hasMicroApp,
  getOidcClientId,
  getFrameworkServiceUrl,
  isOidcClient,
  stateRef,
} from '../../framework/states'
import { lazyImportWebComponent } from '../../framework/webComponent'
import {
  ASC_PORTAL_MICRO_APP,
  ASC_TIER_1_SUPPORT_ROLE,
  CN_REGION,
  FAWKES_APP_ID,
  FAWKES_MICRO_APP_NAME,
  MSP_PATH_PREFIX,
  MULTI_TENANT_PATH_PREFIX_REGEX,
  SCM_URL_KEY,
  RUNNING,
  SASE_CLIENT_ID,
  SASE_CN_CLIENT_ID,
  SASE_CN_URL_KEY,
  SASE_URL_KEY,
  SDWAN_APP_ID,
  SCM_CLIENT_ID,
  MSP_MICRO_APP_NAME,
  GOV_REGION,
  MSP_URL_KEY,
  ASC_PATH_PREFIX,
  SPARKY_FRAMEWORK,
  SCM_ONE_NAV_APP_NAME,
} from '../../framework/constants'
import { showLoadingMask } from '../../framework/loadingMask'
import { getOverrideTsgView, isTsgGovSase } from '../../framework/overrideInstances'
import { RedirectionViews } from '../../framework/enums'
import { getExtensions, resolveExtensions } from '../../framework/extensions/init'
import { createTimeoutPromise, ensureArray, isBoolean, isFunction, isNonemptyArray, isNonemptyObject, isPromiseLike, isString, noop, uniq } from '../../framework/utils'
import { __error, __warn, __info, __isLocalhost } from '../../init/debug'
import { _timeouts, _timing, measureNetworkCondition } from '../../init/performance'
import { getDatadogRum } from '../../framework/externals'
import { addDataDogTimingOnce } from './utils'
import { FetchFn, fetchWithEnforcedTimeout, ffUseNav3, getRouteExtensions, resetExtensionsCache, runIsAllowed } from '../../framework/extensions/utils'
import { getMicroAppVars } from '../../framework/microApps'
import { setValuesInternal, setValuesInternalAndWait, type AuthUpdate } from '../../framework/actions'
import {
  SPARKY_MFE_TSG_VERSION_SELECT_EXTPOINT,
  SPARKY_NAV3_BOTTOM_LEFT_EXTPOINT,
  SPARKY_NAV3_BOTTOM_MOST_LEFT_EXTPOINT,
  SPARKY_NAV3_LEFT_NAV_EXTPOINT,
  SPARKY_NAV3_LEFT_NAV_TPL_EXTPOINT,
  SPARKY_NAV3_REDIRECTS_EXTPOINT,
  SPARKY_NAV3_TSG_EXPOSES_COMPAT_EXTPOINT,
  SPARKY_NAV3_TSG_STATE_LOADERS_EXTPOINT,
  SPARKY_MFE_TSG_PREFILTER_EXTPOINT,
  SPARKY_NAV3_DYNAMIC_BOTTOM_LEFT_EXTPOINT,
  SPARKY_NAV3_DYNAMIC_TOP_LEFT_EXTPOINT,
  type ExposesNs,
  type LeftNavItem,
  type TopLeftNavItem,
  type TsgExposes,
  type SparkyMfeVersionMeta,
  type FetcherParams,
  type SparkyMfePrefilterConfig,
} from '../../extpoints'
import type { ComponentType } from 'react'
import type { AnyState, Authed, AuthedCallback, AuthState, Vars } from '../../framework/stateTypes'
import type {
  LazyFactory,
  AvailFunc,
  MicroAppExported,
  InitedMicroAppConfig,
  InitedMicroApp,
  NavItem,
  NavAction,
  NavAppAttr,
  NavParams,
  Exposes,
  MicroAppExportedOrPromise,
  StoreState,
  AvailParam,
  ComponentOrLazyFactory,
  NavItemArray,
  NavItemComponent,
  SparkyAvailFunc,
} from '../../framework/microAppTypes'
import type {
  TsgUnloadFn,
  TsgLoadFn,
  TsgLoadFnParam,
  TsgUnloadFnParam,
  TsgUnloadResult,
} from '../../framework/newMicroAppTypes'

type MicroAppCfgOrPromise = InitedMicroApp | (Promise<InitedMicroApp> & NavAppAttr)
type NavigationCfg = InitedMicroApp[] & {
  pathAvailMap: Record<string, AvailFunc>
  flattenNavigationList: NavItem[]
  actions: NavAction[]
}
type MicroAppNameAndId = { appName?: string, microAppId?: string }

const isComp = (Comp: ComponentType): boolean => Boolean(
  Comp.displayName || Comp.propTypes || Comp.defaultProps || // eslint-disable-line react/forbid-foreign-prop-types
  Comp.prototype?.isReactComponent || Comp.prototype?.render
)
const isLazyOrSuspense = (Comp: ComponentType | LazyFactory): boolean => Boolean(
  isLazy(Comp) || isSuspense(Comp) || (Comp as { $$typeof?: symbol }).$$typeof === Lazy
)

export function hasExports<T = any>(exports: T): exports is NonNullable<T> {
  if (!exports) {
    return false
  }
  if (Array.isArray(exports)) {
    return exports.length > 0
  }
  for (const _ in exports) {
    return true
  }
  if (typeof ((exports as any).then || exports) === 'function') { // promise or function
    return true
  }
  return false
}

const autoWrapLazy = (Comp: ComponentType | LazyFactory | string | undefined, item: { lazy?: boolean, layout?: string }) => {
  if (!Comp) {
    return
  }
  const lazyComp = item.lazy ?? (Comp as ComponentOrLazyFactory).lazy
  if (lazyComp === false || isString(Comp)) {
    return Comp
  }
  if (lazyComp === true ||
    (isFunction(Comp) && !isLazyOrSuspense(Comp) && !(Comp as { noLazy?: boolean }).noLazy && !isComp(Comp as ComponentType))) {
    // expecting `() => import(...)`
    const newComp: ComponentOrLazyFactory = lazy(Comp as LazyFactory)
    const { layout = item.layout } = Comp as ComponentOrLazyFactory
    if (layout) {
      newComp.layout = layout
    }
    return newComp
  }
  return Comp
}
const autoWrapLazyWithMicroAppAttr = (component: NavItemComponent | undefined, child: NavItem) => {
  if (!component) {
    return component
  }
  const { appName, microAppId } = (component as MicroAppNameAndId)
  const lazyComp = autoWrapLazy(component, child)
  return Object.defineProperties(lazyComp, {
    appName: { value: appName },
    microAppId: { value: microAppId },
  })
}
const traverseChildren = (ch: NavItem[], nav: NavItem) => {
  const execChildren = (children: NavItem[] | undefined, item: NavItem) => {
    if (isFunction(children)) {
      try {
        return children(item)
      }
      catch (e) {
        __error('Error generating dynamic children', item, e)
        return undefined
      }
    }
    return children
  }
  return execChildren(ch, nav).map(({ children, component, ...child }: NavItem) => {
    if (component) {
      child.component = autoWrapLazyWithMicroAppAttr(component, child)
    }
    children = execChildren(children, child)
    if (Array.isArray(children)) {
      child.children = traverseChildren(children, child)
    }
    return child
  })
}
const normalizeNav = ({ featureAvil, children, component, navComponent, ...nav }: NavAction | NavItem): NavItem => ({
  className: 'parent-node',
  ...nav,
  children: children && traverseChildren(children, nav),
  component: autoWrapLazyWithMicroAppAttr(component, nav),
  navComponent: autoWrapLazyWithMicroAppAttr(navComponent, nav),
})

const getFlattenNavigationList = (list: NavItem[]): NavItem[] => (
  list.reduce((r, { children, ...rest }) => {
    r.push(rest)
    if (children) r.push(...getFlattenNavigationList(children))
    return r
  }, [] as NavItem[])
)

const getPathAvailMap = (list: NavItem[]) => (
  list.reduce((map, { path, avail }) => {
    if (path && avail) {
      map[path] = avail
    }
    return map
  }, {} as Record<string, AvailFunc>)
)

export const copyNavAppAttr = (target: any, source: NavAppAttr) => {
  if (!target || !source) {
    return target
  }
  const { id, appName, appVars, varName } = source as any
  target.id = id || appName
  target.appName = appName
  target.appVars = appVars
  if (varName) { // TODO: remove once format updated
    target.varName = varName
  }
}

export const resolveMicroApps = async (apps: MicroAppExportedOrPromise[]): Promise<InitedMicroApp[]> => {
  const initMicroApp = (app: MicroAppExportedOrPromise): Promise<InitedMicroApp | undefined> | InitedMicroApp | undefined => {
    if (!isFunction(app)) {
      return hasExports(app) ? app as InitedMicroApp : undefined
    }
    const onAuthedReplaced = (cb: AuthedCallback) => {
      if (!cb) {
        return
      }
      onAuthed((resolved: Authed) => {
        if (ffUseNav3()) {
          __info(`skipped trigger onAuthed callback for ${app.microAppId || app.id} as nav v3 enabled`)
        }
        else {
          cb(resolved)
        }
      })
    }
    const params = Object.assign(Object.create(libs), {
      id: app.id,
      name: app.appName,
      vars: app.appVars,
      importLibs,
      onAuthed: onAuthedReplaced,
      lazyImportWebComponent,
    }) as NavParams
    try {
      const navApp = app(params) as InitedMicroApp | null | undefined | Promise<InitedMicroApp | null | undefined> & NavAppAttr
      if (!hasExports(navApp)) {
        return undefined
      }
      copyNavAppAttr(navApp, app)
      const { exposes, appName, id: microAppId = appName } = app as InitedMicroAppConfig
      const mergeExposesFromApp = (nav: any) => {
        if (nav && exposes) {
          let mergedExposes = { ...exposes, ...(nav as InitedMicroAppConfig).exposes }
          if (!mergedExposes.default && app.appName === FAWKES_MICRO_APP_NAME) {
            mergedExposes = { default: mergedExposes }
          }
          Object.assign(nav, { exposes: mergedExposes })
        }
        return nav as InitedMicroApp
      }
      const navAppPromise = navApp as Promise<InitedMicroApp>
      if (isFunction(navAppPromise?.then)) {
        const [timeoutPromise, clear = noop] = _timeouts.microAppInitTimeout > 0 ?
          createTimeoutPromise(_timeouts.microAppInitTimeout) : []
        const promise = navAppPromise.then((nav: InitedMicroApp) => {
          clear()
          if (!hasExports(nav)) {
            return undefined
          }
          copyNavAppAttr(nav, navApp as NavAppAttr)
          mergeExposesFromApp(nav)
          return nav as InitedMicroApp
        }, (error) => {
          clear()
          console.error(`failed to init micro-app ${microAppId}`, error)
          return { id: navApp.id, appName: navApp.appName, error } as InitedMicroApp
        })
        return timeoutPromise ? Promise.race([promise, timeoutPromise]).catch((error) => {
          console.error(`failed to init micro-app ${microAppId}`, error)
          if (error?.timeout) {
            __error('please check in browser network tab if any API is pending or take more than 5 seconds, or if there are some static assets take more than 1s to download')
          }
          else {
            __error('please check in browser network tab if there is any API or asssets failed to load, and check the error stack above if it directly from the micro-app')
          }
        }) as Promise<InitedMicroApp> : promise
      }
      return mergeExposesFromApp(navApp as InitedMicroApp) as InitedMicroApp
    }
    catch (error) {
      console.error(`failed to init micro-app ${app.id || app.appName}`, error)
      return { id: app.id, appName: app.appName, error } as InitedMicroApp
    }
  }
  const handleAppErr = (err: Error) => {
    console.error(`micro-app ${(err as any).app || ''} error:`, err)
  }
  const navApps = apps.map((app: MicroAppExportedOrPromise) => {
    const { initedApp: inited, appName, id = appName } = app as any
    if (inited) {
      return inited
    }
    const starts = Date.now()
    const initedApp = (app as Promise<MicroAppExported>)?.then?.(initMicroApp, handleAppErr) || initMicroApp(app)
    Object.defineProperty(app, 'initedApp', { value: initedApp }) // ensure only init app once

    Promise.resolve(initedApp).then(() => {
      const ends = Date.now()
      Object.assign(_timing.micro_apps[id] || (_timing.micro_apps[id] = {}), {
        init_start: starts,
        init_end: ends,
        init_duration: ends - starts,
      })
    })
    return initedApp
  }) as MicroAppCfgOrPromise[]
  const navAppsResolved = (
    navApps.some(app => (app as Promise<InitedMicroApp>)?.then) ? await Promise.all(navApps) : navApps
  ).filter(Boolean) as InitedMicroApp[]
  return navAppsResolved
}

const getRedirectionViewForTSG = (auth: AuthState | undefined): RedirectionViews => {
  const fxVars = getFrameworkVars()
  if (!auth?.instances || fxVars.hub || fxVars.aiops_free || fxVars.tsg_auto_redirection === false) {
    return RedirectionViews.DEFAULT // skip redirection
  }

  if (auth && isTsgGovSase(auth)) {
    return RedirectionViews.SASE
  }

  const getRedirectionViewFromInstances = () => {
    const paInst = auth.instances?.get(FAWKES_APP_ID)

    if (paInst?.status === RUNNING) {
      switch (paInst.platform_region || paInst.region) {
        case CN_REGION:
          return auth.instances?.flags?.is_pa_cn_l2 ? RedirectionViews.SCM_CN : RedirectionViews.SASE_CN
        case GOV_REGION:
          return fxVars.redirect_fedmod_to_scm || new Set(fxVars.fedmod_tsgs_in_scm).has(auth.tsg_id) ? RedirectionViews.SCM : RedirectionViews.SASE
      }
    }

    if (!paInst?.runtime_attributes || paInst.status !== RUNNING) {
      const sdwanInst = auth.instances?.get(SDWAN_APP_ID)
      if (sdwanInst?.status === RUNNING) {
        if (sdwanInst.runtime_attributes?.is_oneapp_ready) {
          return RedirectionViews.SCM
        }
        if (fxVars.redirect_sase_to_scm === 'exclude-sdwan' && fxVars.sase) {
          return RedirectionViews.SASE // redirect sase for sdwan only case
        }
        if (fxVars.redirect_sase_to_scm) {
          return RedirectionViews.SCM
        }
        return RedirectionViews.SASE
      }

      return RedirectionViews.DEFAULT
    } // otherwise pa is running with runtime_attributes

    const isOneappReady = paInst?.runtime_attributes?.is_oneapp_ready
    if (!isOneappReady && !fxVars.redirect_sase_to_scm) {
      return RedirectionViews.SASE
    }

    return RedirectionViews.SCM
  }

  return getOverrideTsgView(auth?.tsg_id) || getRedirectionViewFromInstances()
}

const handleRedirectionView = (view: RedirectionViews, { tsg_id, tsgs, access }: AuthState) => {
  const fxVars = getFrameworkVars()
  if (__isLocalhost || fxVars.hub || fxVars.aiops_free || fxVars.tsg_auto_redirection === false) {
    return // skip redirection
  }

  const tsgId = tsg_id!
  const curPath = window.location.pathname
  const curClientId = getOidcClientId()
  const scm_client_id = fxVars.netsec_client_id || SCM_CLIENT_ID
  const expectedClientId = {
    [RedirectionViews.DEFAULT]: curClientId,
    [RedirectionViews.SASE]: SASE_CLIENT_ID,
    [RedirectionViews.SCM]: scm_client_id,
    [RedirectionViews.HYBRID]: scm_client_id,
    [RedirectionViews.SCM_CN]: scm_client_id,
    [RedirectionViews.SASE_CN]: SASE_CN_CLIENT_ID,
  }[view]

  if ((MULTI_TENANT_PATH_PREFIX_REGEX.test(curPath) ||
    __isLocalhost ||
    fxVars.hub ||
    fxVars.aiops_free ||
    fxVars.tsg_auto_redirection === false) &&
    (curPath !== MSP_PATH_PREFIX && !curPath.startsWith('/msp/') && !(expectedClientId && isOidcClient(expectedClientId)))
  ) {
    return
  }
  if (curPath === '/') {
    if (hasMicroApp(ASC_PORTAL_MICRO_APP) && access?.hasRoleOnly(ASC_TIER_1_SUPPORT_ROLE)) {
      return // which will redirect to /asc
    }
    if (tsgs?.hasParentOrSubTenants(tsgId) && hasMicroApp(MSP_MICRO_APP_NAME)) {
      return // which will redirect to /msp
    }
  }
  if (view === RedirectionViews.SASE && isOidcClient(scm_client_id) && curPath === '/aiops') {
    return // skip redirect to sase for aiops
  }
  if (expectedClientId === SASE_CN_CLIENT_ID &&
    (curPath.startsWith(MSP_PATH_PREFIX) || curPath.startsWith(ASC_PATH_PREFIX))) {
    // APPORTAL-12445: do not allow redirect to cn FQDN for /msp or /asc
    const baseUrl = getFrameworkServiceUrl(MSP_URL_KEY)
    const { pathname, search, hash, href } = window.location
    if (baseUrl && href.startsWith(baseUrl)) {
      return
    }
    const url = new URL(`${pathname}${search}${hash}`, baseUrl)
    url.searchParams.set('tsg_id', tsgId)
    showLoadingMask()
    window.location.href = url.toString()
    throw new Error('redirection')
  }
  if (expectedClientId && !isOidcClient(expectedClientId)) {
    const urlKey = {
      [SASE_CLIENT_ID]: SASE_URL_KEY,
      [SCM_CLIENT_ID]: SCM_URL_KEY,
      [scm_client_id]: SCM_URL_KEY,
      [SASE_CN_CLIENT_ID]: SASE_CN_URL_KEY,
    }[expectedClientId]!
    const baseUrl = getFrameworkServiceUrl(urlKey)
    if (baseUrl && baseUrl !== window.location.origin) {
      __info(`Redirecting to ${view} view client id ${expectedClientId} for TSG from client id ${curClientId}`)
      const { pathname, search, hash } = window.location
      const url = new URL(`${pathname}${search}${hash}`, baseUrl)
      url.searchParams.set('tsg_id', tsgId)
      showLoadingMask()
      window.location.href = url.toString()
      throw new Error('redirection')
    }
  }
}

const filterInitedMicroApps = (microApps: InitedMicroApp[], auth: AuthState | undefined): InitedMicroApp[] => {
  if (getFrameworkVar('hub') || !microApps.length) {
    return microApps // skip
  }
  const redirectionView = getRedirectionViewForTSG(auth)
  if (auth?.tsg_id) {
    handleRedirectionView(redirectionView, auth) // will handle msp sase use case
    if (auth.instances?.flags) {
      auth.instances.flags.redirectionView = redirectionView
    }
  }
  const extEnabled = getFrameworkVar<boolean>('extensibility')
  const disabledMicroAppIds = [] as string[]
  const disabledMicroAppNames = [] as string[]
  let prefilteredMicroApps = microApps
  const prefilterExtensions = extEnabled ? getExtensions(SPARKY_MFE_TSG_PREFILTER_EXTPOINT) : undefined
  const versionExtensions = extEnabled ? getExtensions(SPARKY_MFE_TSG_VERSION_SELECT_EXTPOINT) : undefined
  const getMfeMeta = ({
    appName: name,
    microAppId = name,
    id = microAppId,
    appVars: vars,
  }: InitedMicroApp) => ({
    name,
    id,
    vars,
    version: vars?.microAppVersion || vars?.version || '',
  } as SparkyMfeVersionMeta)
  if (isNonemptyArray(prefilterExtensions)) {
    const filteredOutAppIdSet = new Set()
    for (const { names, filter, isAllowed, microAppId } of prefilterExtensions as (SparkyMfePrefilterConfig & { microAppId: string })[]) {
      if (!runIsAllowed(isAllowed) || (Array.isArray(names) && !names.length)) {
        continue // skip if not allowed or empty array
      } // null/falsy value means all micro-apps
      const microAppNames = uniq(ensureArray(names))
      if (microAppNames.length && !microAppNames.some(hasMicroApp)) {
        continue
      }
      const filteredMfe = !microAppNames.length ? microApps :
        microApps.filter(app => app.appName && microAppNames.includes(app.appName))
      const mfeMetas = filteredMfe.map(getMfeMeta)
      for (const meta of mfeMetas) {
        try {
          if (!filter(meta, stateRef.current, mfeMetas)) {
            filteredOutAppIdSet.add(meta.id)
          }
        }
        catch (err) {
          console.error(`mfe prefilter extension from ${microAppId} for ${microAppNames.join(',') || 'all micro-apps'} throw error:`, err, meta)
        }
      }
    }
    if (filteredOutAppIdSet.size === microApps.length) {
      console.error('mfe prefilter extensions filtered out all micro-apps')
      return []
    }
    else if (filteredOutAppIdSet.size) {
      prefilteredMicroApps = microApps.filter(({
        appName,
        microAppId = appName,
        id = microAppId,
      }) => !filteredOutAppIdSet.has(id))
      __info(`disabled micro-apps for TSG ${auth?.tsg_id} during prefilter: ${Array.from(filteredOutAppIdSet)}`)
    }
  }
  if (isNonemptyArray(versionExtensions)) {
    for (const { name, select, isAllowed } of versionExtensions) {
      if (!name || !hasMicroApp(name) || !runIsAllowed(isAllowed)) {
        continue
      }
      const mfeVersions = prefilteredMicroApps.filter(app =>
        app.appName === name
      ).map(getMfeMeta)
      if (!isNonemptyArray(mfeVersions)) { // not found
        continue
      }

      // warnings for missing or duplicated version
      const existingVerions = new Map<string, string>()
      for (const { id, version } of mfeVersions) { // TODO: check from shell pipeline
        if (!version) {
          __warn(`Micro-app name ${name} id ${id} version is missing`)
        }
        else {
          const existing = existingVerions.get(version)
          if (existing) { // TODO: should fail the update pipeline
            __warn(`Micro-app name ${name} has duplicate version ${version} from ids ${existing} and ${id}`)
          }
          else {
            existingVerions.set(version, id)
          }
        }
      }

      try {
        const selectedVersionResult = select(mfeVersions, stateRef.current)
        const microAppId = !selectedVersionResult ? '' :
          isString(selectedVersionResult) ? selectedVersionResult : selectedVersionResult.id
        const selectedVersion = microAppId ? mfeVersions.find(version => version.id === microAppId) : undefined
        if (!selectedVersion) {
          __info(`disabled all verions for micro-app name ${name}`)
          disabledMicroAppNames.push(name)
        }
        else {
          __info(`selected micro-app id ${selectedVersion.id} version ${selectedVersion.version} for micro-app name ${selectedVersion.name}`)
          for (const mfeVersion of mfeVersions) {
            if (mfeVersion !== selectedVersion) {
              disabledMicroAppIds.push(mfeVersion.id)
            }
          }
        }
      }
      catch (e) {
        console.error(`error occurred while micro-app version selection for micro-app ${name}:`, e)
        // TODO: datadog
      }
    }
  }

  if (!disabledMicroAppIds.length && !disabledMicroAppNames.length) {
    return prefilteredMicroApps
  }
  const filteredOutMicroAppIds: string[] = []
  const filteredMicroApps = prefilteredMicroApps.filter((microapp) => {
    const microAppName = microapp.appName!
    const microAppId = microapp.id || microAppName
    if (!disabledMicroAppIds.includes(microAppId) &&
      !disabledMicroAppNames.includes(microAppName)) {
      return true
    }
    filteredOutMicroAppIds.push(microAppId)
    return false
  })
  __info(`disabled micro-apps for TSG ${auth?.tsg_id}:`, {
    ids: disabledMicroAppIds,
    names: disabledMicroAppNames,
  })
  return filteredMicroApps
}

export const mapNavItemsRecursive = <T extends { children?: T[] } = NavItem>(navItem: NavItem, callback: (navItem: NavItem, parent?: T) => T): T => {
  const copied = callback(navItem)
  if (Array.isArray(navItem.children)) {
    const cb = (child: NavItem) => callback(child, copied)
    copied.children = navItem.children.filter(Boolean).map(item =>
      mapNavItemsRecursive(item, cb)
    ) as T[]
  }
  return copied
}

export const eachNavItemsRecursive = (navItem: NavItem, callback: (navItem: NavItem, parent?: NavItem) => NavItem) => {
  callback(navItem)
  if (Array.isArray(navItem.children)) {
    const cb = (child: NavItem) => callback(child, navItem)
    navItem.children.filter(Boolean).forEach(item =>
      mapNavItemsRecursive(item, cb)
    )
  }
}

export const loadNav3FromExtensions = async ({ auth, extensionCandidatesPromise }: Partial<StoreState>, initedMicroApps: InitedMicroApp[]): Promise<InitedMicroApp[]> => {
  const filteredInitedMicroApps = filterInitedMicroApps(initedMicroApps, auth)
  const microAppIds = filteredInitedMicroApps.map(mfe => mfe.id || mfe.microAppId || mfe.appName)
  microAppIds.unshift(SPARKY_FRAMEWORK)
  if (extensionCandidatesPromise) {
    try {
      await resolveExtensions(filteredInitedMicroApps, await extensionCandidatesPromise)
    }
    catch (err) {
      __error('failed to resolve extensions', err)
    }
  }
  // console.error('>>>filteredInitedMicroApps', filteredInitedMicroApps)
  const [nav3Tpl] = getExtensions(SPARKY_NAV3_LEFT_NAV_TPL_EXTPOINT)
  const nav3LeftNavExts = getExtensions(SPARKY_NAV3_LEFT_NAV_EXTPOINT)
  const nav3BottomLeftNavExts = getExtensions(SPARKY_NAV3_BOTTOM_LEFT_EXTPOINT)
  const nav3DynBottomLeftNavExts = getExtensions(SPARKY_NAV3_DYNAMIC_BOTTOM_LEFT_EXTPOINT)
  const nav3DynTopLeftNavExts = getExtensions(SPARKY_NAV3_DYNAMIC_TOP_LEFT_EXTPOINT)
  const [nav3BottomMostExt] = getExtensions(SPARKY_NAV3_BOTTOM_MOST_LEFT_EXTPOINT)
  const nav3ExposesExts = getExtensions(SPARKY_NAV3_TSG_EXPOSES_COMPAT_EXTPOINT)
  const nav3StateLoaderExts = getExtensions(SPARKY_NAV3_TSG_STATE_LOADERS_EXTPOINT)
  __info('nav v3 extensions', {
    nav3Tpl,
    nav3LeftNavExts,
    nav3BottomLeftNavExts,
    nav3DynBottomLeftNavExts,
    nav3DynTopLeftNavExts,
    nav3BottomMostExt,
    nav3ExposesExts,
    nav3StateLoaderExts,
  })
  const microApps = {} as Record<string, InitedMicroAppConfig>
  const stateLoadingPromises = {} as Record<string, Promise<any> | undefined>
  const stateLoaded = {} as Record<string, any>

  const getMicroAppMeta = (ext: any): { microAppId: string, appName: string, vars?: Vars } => ({
    microAppId: ext.microAppId,
    appName: ext.microAppName,
    vars: getMicroAppVars(ext.microAppId),
  })
  const getMicroAppInMap = (ext: unknown, meta = getMicroAppMeta(ext)) => {
    const microApp = microApps[meta.microAppId] ||
      (microApps[meta.microAppId] = { ...meta } as InitedMicroAppConfig)
    return microApp
  }

  const params = {
    state: stateRef.current,
    stateRef,
    fxVars: stateRef.current.fxVars!,
    sparkyFramework: libs.sparkyFramework,
    fetch: fetchWithEnforcedTimeout,
  } as FetcherParams
  const dummyFetch: FetchFn = () =>
    Promise.reject(new Error('Fetch not allowed when requireFetch is not true'))
  const handleError = (error: Error, microAppId: string, name: string) => {
    const message = `Error loading state ${name} for micro app ${microAppId}:`
    if (error.message) {
      error.message = `${message} ${error.message}`
    }
    console.error(message, error)
    getDatadogRum()?.addError(error, {
      type: 'client_error',
      micro_app_id: microAppId,
    })
  }
  const usedStateNameMap = new Map(stateRef.current._initFxStateKeys.map((key: string) => [key, SPARKY_FRAMEWORK]))
  for (const ext of nav3StateLoaderExts) {
    if (runIsAllowed(ext.isAllowed)) {
      const microAppMeta = getMicroAppMeta(ext)
      const { name, requireFetch, loader } = ext
      if (usedStateNameMap.has(name)) {
        const msg = `state loader name "${name}" from micro-app "${microAppMeta.microAppId}" already used by another micro-app ${usedStateNameMap.get(name)}`
        console.error(`${msg}, skipping loader, please use a unique name.`)
        getDatadogRum()?.addError(new Error(msg), {
          type: 'client_error',
          micro_app_id: microAppMeta.microAppId,
        })
        continue
      }
      const runStateLoader = () => { // ensure return promise
        try {
          const result = loader({
            ...params,
            name,
            fetch: requireFetch ? fetchWithEnforcedTimeout : dummyFetch,
          })
          if (result === undefined || !isPromiseLike(result)) {
            return result
          }
          else {
            return result.then((res) => res, (err: Error) => {
              handleError(err as Error, microAppMeta.microAppId, name)
            })
          }
        }
        catch (err) {
          handleError(err as Error, microAppMeta.microAppId, name)
        }
      }
      const resultOrPromise = runStateLoader()
      usedStateNameMap.set(name, microAppMeta.microAppId)
      const microApp = getMicroAppInMap(ext, microAppMeta)
      if (isPromiseLike(resultOrPromise)) {
        stateLoadingPromises[name] = resultOrPromise as Promise<any>
        const promises = microApp.stateLoadingPromises ||
          (microApp.stateLoadingPromises = {} as Record<string, Promise<any>>)
        promises[name] = resultOrPromise as Promise<any>
      }
      else if (resultOrPromise) {
        stateLoaded[name] = resultOrPromise
        const state = microApp.state || (microApp.state = {} as AnyState)
        state[name] = resultOrPromise
      }
    }
  }

  const COMMON_NAV_PROPS = {
    asPlaceholder: true,
    mergeNav: true
  }
  const filterInvalidNavItem = (navItem: LeftNavItem): boolean => {
    if (navItem.asPlaceholder) {
      __error(`asPlaceholder is no longer supported in nav v3 items ${navItem.key || navItem.path}, please remove those`, navItem)
      return false
    }
    return true
  }
  const applyDefaultsRecursive = (navItem: LeftNavItem, commonProps: Record<string, any>): NavItem => {
    const copied = { key: navItem.key || navItem.path, ...navItem, ...commonProps } as NavItem
    if (copied.component) {
      __warn(`component should not be assigned in nav v3 items ${navItem.key || navItem.path}, please move component to the route extension`, navItem)
      delete copied.component
    }
    if (Array.isArray(navItem.children)) {
      copied.children = navItem.children.filter(filterInvalidNavItem).map(item =>
        applyDefaultsRecursive(item, commonProps)
      )
    }
    return copied
  }
  const applyCommonProps = (navItems: TopLeftNavItem[], commonProps: Record<string, any>): NavItem[] => {
    return navItems.filter(filterInvalidNavItem).map(item =>
      Object.assign(applyDefaultsRecursive(item, commonProps), { mergeNav: true })
    )
  }
  const applyCommonPropsForTpl = (navItems: TopLeftNavItem[], commonProps: Record<string, any>): NavItem[] => {
    const applyDefaultsFn = (navItem: TopLeftNavItem, commonProps: Record<string, any>): NavItem => {
      const copied = applyDefaultsRecursive(navItem, commonProps)
      if (navItem.icon) {
        Object.defineProperty(navItem.icon, '__approved_icon', {
          value: true
        })
      }
      Object.defineProperty(navItem, '__approved_item', {
        value: true
      })
      return copied
    }
    return navItems.filter(filterInvalidNavItem).map(item =>
      Object.assign(applyDefaultsFn(item, commonProps), { mergeNav: true })
    )
  }

  const getStateLoadingPromisesInCommonProps = (ext: { stateLoaders?: string[] }, microAppMeta: Record<string, any>): Record<string, any> => {
    if (isNonemptyArray(ext.stateLoaders)) {
      const promises = {} as Record<string, Promise<any>>
      for (const name of ext.stateLoaders) {
        if (['auth', 'main'].includes(name)) {
          continue // ignore those for now
        }
        const promise = stateLoadingPromises[name]
        if (promise) {
          promises[name] = promise
        }
        else if (!stateLoaded[name]) {
          console.error(`cannot find state loader '${name}' for microapp '${microAppMeta.microAppId}'`)
        }
      }
      if (isNonemptyObject(promises)) {
        microAppMeta.stateLoadingPromises = promises
      }
    }
    return microAppMeta
  }

  const tplRootItemMap = {} as Record<string, TopLeftNavItem>
  const filterTplRootItem = (item: NavItem) => {
    const rootItemByPath = item.path && item.path.length > 1 ? tplRootItemMap[item.path] : undefined
    const rootItemByKey = item.key ? tplRootItemMap[item.key] : undefined
    const itemMsg = item.key?.startsWith('/') && item.key === item.path ? `'${item.microAppId}' path '${item.path}'` : `'${item.microAppId}' key '${item.key}' with path '${item.path}'`
    if (rootItemByPath && rootItemByKey && rootItemByPath !== rootItemByKey) {
      __error(`ignore nav item from ${itemMsg} not matching the approved values ${rootItemByKey.key} ${rootItemByKey.path} or ${rootItemByPath.key} ${rootItemByPath.path}`)
      return false
    }
    else if (rootItemByKey && item.path && rootItemByKey.path !== item.path) {
      __error(`ignore nav item from ${itemMsg} not matching the approved path ${rootItemByKey.path}`)
      return false
    }
    else if (rootItemByPath && item.key && rootItemByPath.key !== item.key && item.key !== item.path) {
      __error(`ignore nav item from ${itemMsg} not matching the approved key ${rootItemByPath.path}`)
      return false
    }
    return true
  }
  if (nav3Tpl) {
    const nav3TplMeta = getMicroAppMeta(nav3Tpl)
    const items = ensureArray(nav3Tpl.navigation())
    const splitIdx = items.findIndex(item => item.key === '-')
    const navigation = splitIdx > 0 ? items.slice(0, splitIdx) : items
    const actions = splitIdx > 0 ? items.slice(splitIdx + 1) : undefined
    for (const item of navigation.concat(actions || [])) {
      if (item.path && item.path.length > 1) {
        tplRootItemMap[item.path] = item
      }
      if (item.key) {
        tplRootItemMap[item.key] = item
      }
    }
    microApps[nav3TplMeta.microAppId] = {
      ...nav3TplMeta,
      navigation: applyCommonPropsForTpl(navigation, COMMON_NAV_PROPS) as NavItem[],
      actions: actions && applyCommonPropsForTpl(actions, COMMON_NAV_PROPS) as NavItem[],
    } as InitedMicroAppConfig
  }

  const nav3TopLeftNavExtsWithDyn = nav3LeftNavExts.concat(nav3DynTopLeftNavExts.map(ext => {
    Object.defineProperties(ext, {
      isDynamic: { value: true },
      navigation: {
        value: () => [ext]
      },
    })
    return ext
  }))
  for (const ext of nav3TopLeftNavExtsWithDyn) {
    if (runIsAllowed(ext.isAllowed)) {
      const microAppMeta = getMicroAppMeta(ext)
      const microApp = getMicroAppInMap(ext, microAppMeta)
      const commonProps = getStateLoadingPromisesInCommonProps(ext, microAppMeta)
      const items = applyCommonProps(ensureArray(ext.navigation()), commonProps).filter(filterTplRootItem)
      if (items?.length) {
        const navigation = microApp.navigation || (microApp.navigation = [] as NavItem[])
        navigation.push(...items)
      }
    }
  }

  const nav3BottomLeftNavExtsWithDyn = nav3BottomLeftNavExts.concat(nav3DynBottomLeftNavExts.map(ext =>
    Object.defineProperty(ext, 'isDynamic', { value: true })
  ))
  for (const ext of nav3BottomLeftNavExtsWithDyn) {
    const microAppMeta = getMicroAppMeta(ext)
    const microApp = getMicroAppInMap(ext, microAppMeta)
    const commonProps = getStateLoadingPromisesInCommonProps(ext, microAppMeta)
    const actions = microApp.actions || (microApp.actions = [] as NavAction[])
    const action = {
      key: ext.key || ext.path,
      ...ext.flags,
      ...ext,
      ...commonProps,
      title: ext.titleComponent || ext.title,
      bottomMost: false,
      mergeNav: true,
      avail: ext.avail || (ext.isAllowed ? () => ext.isAllowed() : undefined) as SparkyAvailFunc | undefined,
    } as NavAction
    if (('isDynamic' in ext) && isFunction(ext.dynamicChildren)) {
      Object.assign(action, {
        dynamicChildren: ext.dynamicChildren,
      })
    }
    else if (action.dynamicChildren) {
      delete action.dynamicChildren
      console.warn('"dynamicChildren" property is not supported for static nav items. Please use "children" instead.', ext)
    }
    actions.push(action)
  }

  if (nav3BottomMostExt && runIsAllowed(nav3BottomMostExt.isAllowed)) {
    const microAppMeta = getMicroAppMeta(nav3BottomMostExt)
    const microApp = getMicroAppInMap(nav3BottomMostExt, microAppMeta)
    const actions = microApp.actions || (microApp.actions = [] as NavAction[])
    actions.push({
      ...nav3BottomMostExt.flags,
      ...nav3BottomMostExt,
      ...microAppMeta,
      title: nav3BottomMostExt.titleComponent || nav3BottomMostExt.title,
      bottomMost: true,
      avail: nav3BottomMostExt.isAllowed ? () => nav3BottomMostExt.isAllowed() : undefined as SparkyAvailFunc | undefined,
    } as NavAction)
  }

  for (const ext of nav3ExposesExts) {
    if (runIsAllowed(ext.isAllowed)) {
      const microApp = getMicroAppInMap(ext)
      const exposes = microApp.exposes as TsgExposes || (microApp.exposes = {} as TsgExposes)
      const exposesResult = ext.exposes()
      if (isNonemptyObject<TsgExposes>(exposesResult)) {
        for (const nsKey of Object.keys(exposesResult)) {
          const ns = exposes[nsKey] || (exposes[nsKey] = {} as ExposesNs)
          Object.assign(ns, exposesResult[nsKey])
        }
      }
    }
  }

  const orderedMicroApps = microAppIds.map(id => id && microApps[id]).filter(Boolean) as InitedMicroApp[]
  __info('microApps config for nav3 exports from exts', orderedMicroApps)
  return Promise.resolve(orderedMicroApps)
}

export const dynamicMicroAppsTsgLoad = ({ auth, main, eargerLoad, extensionCandidatesPromise }: Partial<StoreState>, initedMicroApps: InitedMicroApp[]): Promise<InitedMicroApp[]> => {
  if (ffUseNav3()) { // ignore early load for now
    if (eargerLoad) {
      return Promise.resolve([])
    }
    return loadNav3FromExtensions({ auth, extensionCandidatesPromise }, initedMicroApps)
  }
  const convertExports = (result: any, original: NavAppAttr) => {
    if (!result) {
      const exposes = (original as InitedMicroAppConfig).exposes
      if (!hasExports(exposes)) {
        return undefined
      }
      result = { exposes }
    }
    const { actions, exposes } = result
    if (Array.isArray(result)) {
      result = {
        navigation: result
      }
    }
    else {
      const nav = result as NavItem
      if (nav.component || Array.isArray(nav.children)) {
        result = {
          navigation: [nav]
        }
      }
      else {
        result = { ...result }
      }
    }
    if (actions) {
      result.actions = Array.isArray(actions) ? actions : [actions]
    }
    const fnExposes = (original as InitedMicroAppConfig).exposes
    if (hasExports(exposes)) {
      result.exposes = fnExposes ? { ...fnExposes, ...exposes } : exposes
    }
    else if (hasExports(fnExposes)) {
      result.exposes = fnExposes
    }
    copyNavAppAttr(result, original)
    const { appName, id: microAppId = appName } = original
    const appProps = {
      appName: { value: appName },
      microAppId: { value: microAppId }
    }
    const defineMicroAppNameIdForComponent = (component: NavItemComponent) => {
      if ((component as MicroAppNameAndId).appName) {
        return component
      }
      return Object.defineProperties(component, appProps)
    }
    const traverseChildren = (ch: NavItem[]) => ch.map(({ children, navComponent, component, avail, ...child }: NavItem): InitedMicroApp => {
      if (child.microAppId) { // already traversed
        return child as InitedMicroApp
      }
      if (component) {
        child.appName = appName
        child.microAppId = microAppId
        child.component = defineMicroAppNameIdForComponent(component)
      }
      if (navComponent) {
        child.navComponent = defineMicroAppNameIdForComponent(navComponent)
      }
      if (isFunction(children)) {
        child.children = () => {
          try {
            const generated = children(child)
            if (isNonemptyArray(generated)) {
              return traverseChildren(generated)
            }
          }
          catch (e) {
            __error(`Error generating dynamic children for microapp ${microAppId}`, child, e)
            return []
          }
        }
      }
      else if (isNonemptyArray(children)) {
        child.children = traverseChildren(children)
      }
      if (isFunction(avail)) {
        child.avail = Object.defineProperties(avail, appProps)
      }
      return child as InitedMicroApp
    })
    if (isNonemptyArray(result.navigation)) {
      result.navigation = traverseChildren(result.navigation)
    }
    if (isNonemptyArray(result.actions)) {
      result.actions = traverseChildren(result.actions)
    }
    return result as InitedMicroApp
  }
  const fnParam = {
    auth,
    main,
  } as TsgLoadFnParam
  const param = {
    ...fnParam,
  } as TsgLoadFnParam
  if (!getFrameworkVar('pdf_mode')) {
    measureNetworkCondition()
  }
  const starts = Date.now()
  _timing.micro_apps_tsg_export_start = starts
  {
    const tsgUnloadFns = new Set<TsgUnloadFn>()
    param.onTsgUnload = (cb: TsgUnloadFn): void => {
      if (!isFunction(cb)) {
        console.error('callback function must be provided for onTsgUnload', cb)
        throw new Error('callback function must be provided for onTsgUnload')
      }
      tsgUnloadFns.add(cb)
    }
    events.once('switch-tsg-unload', (update: AuthUpdate) => {
      const tsgUnloadFnArray = Array.from(tsgUnloadFns)
      if (!tsgUnloadFnArray.length) {
        return
      }
      const param = { ...fnParam } as TsgUnloadFnParam
      update.waitPromise = Promise.all(tsgUnloadFnArray.map(async (fn) => {
        try {
          return await fn(param)
        }
        catch (error) {
          console.error('failed to run tsg unload callback', error)
        }
      })).then((results) => {
        const forceReload = results.some(res => res?.forceReload)
        return { forceReload } as TsgUnloadResult
      })
    })
  }

  const runOnload = async (loadFn: TsgLoadFn): Promise<InitedMicroApp | undefined> => {
    const [timeoutPromise, clear = noop] = _timeouts.microAppTsgLoadTimeout > 0 ?
      createTimeoutPromise(_timeouts.microAppTsgLoadTimeout) : []
    const { appName, id, microAppId = id || appName } = loadFn as any
    const starts = Date.now()
    try {
      const loadPromise = loadFn(param)
      const result = await (timeoutPromise ? Promise.race([
        loadPromise,
        timeoutPromise,
      ]) : loadPromise) as InitedMicroApp
      clear()
      if (!result) { // if tsg load fn return fasly value, even if it has static exports, skip it
        return undefined
      }
      return convertExports(result, loadFn as NavAppAttr)
    }
    catch (e: any) {
      clear()
      console.error('failed to execute tsg load function', microAppId, e)
      getDatadogRum()?.addError(new Error(`failed to execute tsg load function for ${microAppId}: ${e.message || e}`), {
        type: 'client_error',
        micro_app_id: microAppId,
      })
      if (e?.timeout) {
        __error('please check in browser network tab if any API is pending or take more than 5 seconds, or if there are some static assets take more than 1s to download')
      }
      else {
        __error('please check in browser network tab if there is any API or asssets failed to load, and check the error stack above if it directly from the micro-app')
      }
      return undefined
    }
    finally {
      const ends = Date.now()
      Object.assign(_timing.micro_apps[microAppId] || (_timing.micro_apps[microAppId] = {}), {
        tsg_load_start: starts,
        tsg_load_end: ends,
        tsg_load_duration: ends - starts,
      })
    }
  }
  const filteredInitedMicroApps = eargerLoad ? initedMicroApps : filterInitedMicroApps(initedMicroApps, auth)
  return Promise.all(filteredInitedMicroApps.map(async (microApp) => {
    try {
      if (isFunction(microApp)) {
        return await runOnload(microApp as TsgLoadFn)
      }
      return await convertExports(microApp, microApp)
    }
    finally {
      const { appName, id, microAppId = id || appName } = microApp as any
      const ends = Date.now()
      const timing = _timing.micro_apps[microAppId] || (_timing.micro_apps[microAppId] = {})
      timing.ready_end = ends
    }
  })).then(microApps => {
    const microAppMap = microApps.reduce((map, microApp) => {
      if (microApp) {
        const existing = map.get(microApp.appName)
        if (existing) {
          console.error(`ignore duplicated micro-app name ${microApp.appName} with different ids`, microApp, existing)
          const microAppId = microApp.microAppId || microApp.id
          getDatadogRum()?.addError(new Error(`ignore duplicated micro-app name ${microApp.appName} with different id ${microAppId}`), {
            type: 'client_error',
            micro_app_id: microAppId,
          })
        }
        else {
          map.set(microApp.appName, microApp)
        }
      }
      return map
    }, new Map())
    const exported = Array.from(microAppMap.values())
    if (eargerLoad) {
      __info('eager load micro-apps', exported)
    }
    else {
      _timing.micro_apps_tsg_export_end = Date.now()
      _timing.micro_apps_tsg_export_duration = _timing.micro_apps_tsg_export_end - starts
      __info(`micro-apps exported after tsg init in ${_timing.micro_apps_tsg_export_duration}ms`, exported)
      addDataDogTimingOnce('micro_apps_tsg_exported', _timing.micro_apps_tsg_export_end)
      addDataDogTimingOnce('micro_apps_tsg_export_duration', _timing.micro_apps_tsg_export_duration)
      for (const microAppId of Object.keys(_timing.micro_apps)) {
        const duration = _timing.micro_apps[microAppId].tsg_load_duration
        if (duration > 500) {
          addDataDogTimingOnce(`micro_app_tsg_load_duration.${microAppId}`, duration)
        }
      }
    }
    return exported
  }).then(async (exported) => {
    if (!eargerLoad && exported && extensionCandidatesPromise) {
      try {
        resetExtensionsCache() // just ensure gc
        await resolveExtensions(exported, await extensionCandidatesPromise)
      }
      catch (err) {
        __error('failed to resolve extensions', err)
      }
    }
    return exported
  })
}

const ensureUseApprovedIcon = (microAppsNav: NavItem[]) => {
  const reduceFn = (o: Record<string, any>, item: NavItem) => {
    if (item?.icon?.__approved_icon) {
      if (item.key && !o[item.key]) {
        o[item.key] = item.icon
      }
      if (item.path?.startsWith('/') && !o[item.path]) {
        o[item.path] = item.icon
      }
    }
    if (item?.overrideLeftNav && isNonemptyArray(item.children)) {
      item.children.reduce(reduceFn, o)
    }
    return o
  }
  const approvedIcons = microAppsNav.reduce(reduceFn, {} as Record<string, any>)
  if (Object.keys(approvedIcons).length) {
    const replaceIcons = (items: NavItem[]) => {
      for (const item of items) {
        if (item.icon && !item.icon.__approved_icon) {
          const replaceIcon = (item.key && approvedIcons[item.key]) ||
            (item.path?.startsWith('/') && approvedIcons[item.path])
          if (replaceIcon) {
            item.icon = replaceIcon
          }
        }
        if (item?.overrideLeftNav && isNonemptyArray(item.children)) {
          replaceIcons(item.children)
        }
      }
    }
    replaceIcons(microAppsNav)
  }
  return microAppsNav
}

export const processMicroApps = (navAppsResolved: InitedMicroApp[]) => {
  const microAppsNav = navAppsResolved.reduce((all, app) => {
    const { appName, appVars, error } = app as NavAppAttr & { error: Error }
    if (error) {
      return all
    }
    const assignAppVars = (app: NavItem) => Object.assign(app, { appName, appVars })
    const pushEach = (app: NavItem) => {
      app = assignAppVars(app)
      if (app.hoistChildren && Array.isArray(app.children)) {
        const { children, ...nav } = app
        all.push(nav, ...children.map(assignAppVars))
      }
      else {
        all.push(app)
      }
    }
    if ((app as InitedMicroAppConfig).navigation) {
      app = (app as InitedMicroAppConfig).navigation as InitedMicroApp
    }
    if (isFunction(app)) {
      app = app() || app
    }
    if (Array.isArray(app)) {
      (app as NavItem[]).forEach(pushEach)
    }
    else if (app) {
      const nav = app as NavItem
      if (nav.component || Array.isArray(nav.children)) {
        pushEach(app as NavItem)
      }
      else if (!nav.path && !nav.exposes && !nav.actions && !nav.extensions && !nav.extensionPoints) {
        console.warn(`invalid nav item exposed from ${app.appName}`, nav)
      } // ignore object with only exposes
    }
    return all
  }, [] as NavItem[])
  const mergeAvail = (target: NavItem, source: NavItem): { avail?: AvailFunc } => {
    if ((!source.avail || isBoolean(source.avail)) && (!target.avail || isBoolean(target.avail))) {
      return {}
    }
    const getAvailAndApplyFlag = (item: NavItem): AvailFunc | undefined => {
      if (isBoolean(item.avail)) { // deal with some mistakes in code
        console.error('navitem.avail should be a function intead of boolean value', item)
        return undefined
      }
      if (!item.avail || !(item.callAvail || item.asPlaceholder || item.isSetup)) {
        return item.avail
      }
      if (item.avail.mergeAvail) {
        return item.avail
      }
      return Object.assign(item.avail, {
        mergeAvail: (isString(item.callAvail) && item.callAvail) ||
          (item.asPlaceholder && 'append') || 'auto'
      })
    }
    const tarAvail = getAvailAndApplyFlag(target)
    const srcAvail = getAvailAndApplyFlag(source)
    if (!srcAvail || tarAvail?.mergeAvail === 'override') {
      return { avail: tarAvail }
    }
    if (!tarAvail || srcAvail?.mergeAvail === 'override') {
      return { avail: srcAvail }
    }
    else if (tarAvail.mergeAvail === 'append' || srcAvail.mergeAvail === 'append') {
      const avail: AvailFunc = (...args: [AvailParam]) => tarAvail(...args) && srcAvail(...args)
      Object.assign(avail, { mergeAvail: 'append', _avails_: [srcAvail, tarAvail] })
      return { avail }
    }
    else if (source.component && !target.component) {
      return { avail: srcAvail }
    }
    else if (target.component && !source.component) {
      return { avail: tarAvail }
    }
    return {}
  }
  const mergeChildren = (children1?: NavItem[], children2?: NavItem[]): NavItem[] | undefined => {
    if (!children1?.length) {
      return children2 || children1 || undefined
    }
    if (!children2?.length) {
      return children1 || children2 || undefined
    }
    const merged = children1.concat(children2).filter(Boolean)
    return merged.length > 1 ? mergeNav(merged, true) : merged
  }
  const mergeNav = (navItems: NavItem[], allowChildrenMerge?: boolean): NavItem[] => {
    const mergeMapping = {} as Record<string, NavItem>
    const newNav = [] as NavItem[]
    for (const item of navItems) {
      if (!item) {
        continue
      }
      const path = item.path !== '#' && item.path
      const existing = (item.key && mergeMapping[item.key]) || (path && mergeMapping[path])
      const allowNavMerge = allowChildrenMerge || item.mergeNav
      if (!existing) { // new
        const newItem = { ...item } // make a copy for allow merge
        if (allowNavMerge) {
          newItem.mergeNav = allowNavMerge
        }
        if (item.key) {
          mergeMapping[item.key] = newItem
        }
        if (path) {
          mergeMapping[path] = newItem
        }
        if (isBoolean(newItem.avail)) {
          newItem.avail = undefined
        }
        newNav.push(newItem)
        continue
      } // else merge in
      const microAppId = item.microAppId || item.appName
      if (!(allowNavMerge && existing.mergeNav) && !(existing.asPlaceholder || item.asPlaceholder)) { // not allow merge
        console.error('WARN: ignore duplicated nav item from', microAppId, item, 'found existing item with same key or path without mergeNav or asPlaceholder', existing, 'from', existing.microAppId || existing.appName, existing.path || existing.key)
        getDatadogRum()?.addError(new Error(`found duplicated nav item ${item.path || item.key} from ${microAppId} with existing item with same key or parth without mergeNav or asPlaceholder ${existing.path || existing.key} from ${existing.microAppId || existing.appName}`), {
          type: 'client_error',
          micro_app_id: microAppId,
        })
        continue
      }
      if (existing.component && item.component && !existing.asPlaceholder) {
        console.error('WARN: ignore merge nav item with both existing and current has conflict component', item, 'existing', existing)
        getDatadogRum()?.addError(new Error(`merge nav item with both existing and current has conflict component ${item.path || item.key} from ${microAppId} and ${existing.microAppId || existing.appName}`), {
          type: 'client_error',
          micro_app_id: microAppId,
        })
        continue
      }
      const { children, ...rest } = item // did a clone to avoid item is not extensible
      if (rest.asPlaceholder) { // placeholder after existing
        if (rest.component) {
          console.error('WARN: as placeholder, component is not allowed', item, 'existing', existing)
          continue
        }
        delete rest.id
        delete rest.appName
        delete rest.appVars
        delete rest.asPlaceholder
        delete rest.contentClassName
        Object.assign(rest, existing, mergeAvail(rest, existing), {
          children: mergeChildren(existing.children, children),
          _placeholder_applied: true,
        }, rest.overrideLeftNav && {
          overrideLeftNav: {
            ...existing.overrideLeftNav,
            ...rest.overrideLeftNav,
          }
        }) // merge to placeholder
        if (existing.asPlaceholder || existing._placeholder_applied) { // already has placeholder applied
          Object.assign(existing, rest)
          rest.asPlaceholder = true // remove the new one instead of existing
          continue
        }
        // then replace the existing mergeMapping
        if (path) {
          mergeMapping[path] = rest
        }
        if (item.key) {
          mergeMapping[rest.key] = rest
        }
        newNav.push(rest)
        existing.asPlaceholder = true // and mark the existing one to remove
        continue
      }
      // start merge
      Object.assign(existing, rest, mergeAvail(existing, rest), existing.component && existing.appName && {
        microAppId: existing.microAppId || existing.id || existing.appName,
        appName: existing.appName,
        appVars: existing.appVars,
        contentClassName: existing.contentClassName,
      }, {
        children: mergeChildren(existing.children, children)
      }, existing.overrideLeftNav && {
        overrideLeftNav: {
          ...existing.overrideLeftNav,
          ...rest.overrideLeftNav,
        }
      })
      if (existing.asPlaceholder) { // existing is placeholder
        existing._placeholder_applied = true
        existing.asPlaceholder = undefined // remove the flag
      }
    }
    const keyMap = {} as Record<string, NavItem>
    return newNav.filter(item => {
      if (item.asPlaceholder || !item.key) {
        return true
      }
      const existing = keyMap[item.key]
      if (existing) {
        console.error('found duplicated key after merge nav', item.key, existing, item)
        return false
      }
      keyMap[item.key] = item
      return true
    })
  }
  const cleanupPlaceholders = (children?: NavItem[]): NavItem[] => {
    const navList = [] as NavItem[]
    return children?.reduce((array, item) => {
      if (!item || item.asPlaceholder) {
        return array // filter out
      }
      if (!Object.isExtensible(item) || Object.isFrozen(item)) {
        item = { ...item } // do clone if item cannot be extended, and ignore non-iteratables
      }
      if (item.children) {
        item.children = cleanupPlaceholders(item.children)
      }
      if (item._placeholder_applied) {
        delete item._placeholder_applied // cleanup the internal flag
      }
      array.push(item)
      return array
    }, navList) || navList
  }

  __info('navigation before merge', microAppsNav)
  const nav = cleanupPlaceholders(mergeNav(ensureUseApprovedIcon(microAppsNav)))
  return nav.map(normalizeNav)
}
export const processActions = (navAppsResolved: InitedMicroApp[]) => {
  const microAppsActions = (navAppsResolved as InitedMicroAppConfig[]).reduce((allActions: NavAction[], app: InitedMicroAppConfig) => {
    if (!app.actions) {
      return allActions
    }
    const { appName, appVars } = app
    const actions = (isFunction(app.actions) && app.actions()) || app.actions
    const addName = (act: NavAction) => Object.assign(act, { appName, appVars })
    if (Array.isArray(actions)) {
      allActions.push(...actions.map(addName))
    }
    else {
      allActions.push(addName(actions))
    }
    return allActions.filter(action => {
      if (!action.key) {
        console.error('action key is required', action)
        return false
      }
      return true
    })
  }, [] as NavAction[])
  const actions = ensureUseApprovedIcon(microAppsActions.map(normalizeNav)) // .filter(action => !action.asPlaceholder)
  return actions as NavAction[]
}
export const processNav = (navAppsResolved: InitedMicroApp[]) => {
  const microAppsNav = processMicroApps(navAppsResolved) as NavItem[]
  const flattenNavigationList = getFlattenNavigationList(microAppsNav)
  const pathAvailMap = getPathAvailMap(flattenNavigationList)
  return Object.assign(microAppsNav, {
    pathAvailMap,
    flattenNavigationList,
  }) as NavigationCfg
}

export const extractExposes = (exposes: Record<string, Exposes>, navAppsResolved: InitedMicroApp[]) => {
  const mergedExposes = { global: {} } as Record<string, Exposes>
  const nss = exposes && Object.keys(exposes)
  if (nss?.length) {
    for (const ns of nss) { // 2 level deep clone
      mergedExposes[ns] = { ...exposes[ns] }
    }
  }
  if (navAppsResolved?.length) {
    const __var_names__ = exposes.__var_names__ || {}
    for (const app of navAppsResolved as InitedMicroAppConfig[]) {
      mergeExposes(mergedExposes, app.exposes, app.appName, __var_names__[app.id || app.appName])
    }
  }
  __info('merged exposes', mergedExposes)
  return mergedExposes
}

const NESTED_PATH_REGEX = /\/(?:\*|:\w+\??)$/

export const generateNavActionExposes = (initedMicroApps: InitedMicroApp[]) => {
  const leftMainNavigation = processNav(initedMicroApps) as NavItemArray
  const SETTINGS_KEY = 'settings'
  const MSP_SETTINGS_KEY = 'msp-settings'
  const values = {
    leftMainNavigation,
    navRoutes: leftMainNavigation,
    microAppsTsgLoadPromise: undefined,
    leftMainActions: undefined,
    exposes: extractExposes(inited.settings.exposes, initedMicroApps),
  } as {
    initedMicroApps?: InitedMicroApp[]
    leftMainNavigation: NavItemArray
    navRoutes: (NavItem | NavAction)[]
    microAppsTsgLoadPromise?: Promise<any>
    leftMainActions?: NavAction[]
    leftMainRoutes?: NavItem[]
    routeExtensions?: StoreState['routeExtensions']
    [key: string]: any
  }
  const fxVars = getFrameworkVars()
  const stateLoadingPromises = {} as Record<string, Promise<any>>
  const leftNavStateLoadingPromises = {} as Record<string, Promise<any>>
  if (ffUseNav3()) {
    for (const { navigation, actions, state, stateLoadingPromises: promises } of initedMicroApps as InitedMicroAppConfig[]) {
      if (state) {
        Object.assign(values, state)
      }
      if (promises) {
        Object.assign(stateLoadingPromises, promises)
      }
      const leftNavItems = ensureArray(navigation).concat(ensureArray(actions))
      for (const nav of leftNavItems) {
        if (nav.stateLoadingPromises) {
          Object.assign(leftNavStateLoadingPromises, nav.stateLoadingPromises)
        }
      }
    }
    if (isNonemptyObject(stateLoadingPromises)) {
      values.stateLoadingPromises = stateLoadingPromises
      for (const name of Object.keys(stateLoadingPromises) as string[]) {
        stateLoadingPromises[name].then((res) => {
          if (stateRef.current[name] !== res) { // TODO: batch or delay
            setValuesInternal({ [name]: res })
          }
        })
      }
    }
    if (isNonemptyObject(leftNavStateLoadingPromises)) {
      // console.error('>>leftNavStateLoadingPromises', leftNavStateLoadingPromises)
      const promises = [] as Promise<any>[]
      for (const name of Object.keys(leftNavStateLoadingPromises) as string[]) {
        promises.push(leftNavStateLoadingPromises[name].then((value) => [name, value]))
      }
      values.leftNavStateLoadingPromise = Promise.all(promises).then(async (pairs) => {
        const diff = {} as Record<string, any>
        for (const [name, value] of pairs) {
          if (stateRef.current[name] !== value) {
            diff[name] = value
          }
        }
        await setValuesInternalAndWait(diff)
        Object.defineProperty(values.leftNavStateLoadingPromise, 'resolved', { value: true })
      }) // no rejections
    }
    else {
      values.leftNavStateLoadingPromise = Promise.resolve()
      Object.defineProperty(values.leftNavStateLoadingPromise, 'resolved', { value: true })
    }
  }

  const actions = processActions(initedMicroApps)
  if (!ffUseNav3() && !fxVars.aiops_free) {
    const cleanup = {
      hidden: undefined,
      avail: undefined,
      callAvail: undefined,
      isLoading: undefined,
      featureAvail: undefined,
      asPlaceholder: undefined,
    } as Partial<NavItem>
    const mergeSettings = (key: string, avail: AvailFunc) => {
      const settings = leftMainNavigation.find(item => item.key === key)
      if (!settings) {
        return
      }
      const placeholder = actions.find(item => item.key === key && item.asPlaceholder && item.appName === SCM_ONE_NAV_APP_NAME) ||
        actions.find(item => item.key === key && item.asPlaceholder)
      const updateAction = {
        ...settings,
        ...cleanup,
        avail,
      } as NavAction
      if (placeholder) {
        if (placeholder.appName === SCM_ONE_NAV_APP_NAME && placeholder.avail) {
          const placeholderAvail = placeholder.avail
          updateAction.avail = (props) => avail(props) && placeholderAvail(props)
        }
        Object.assign(placeholder, updateAction)
      }
      else {
        actions.unshift(updateAction)
      }
      settings.hidden = true
    }

    mergeSettings(SETTINGS_KEY, hasMicroApp(ASC_PORTAL_MICRO_APP) ?
      (({ access, location }: any) =>
        !location?.pathname?.startsWith(MSP_PATH_PREFIX) &&
        !access?.hasRoleOnly(ASC_TIER_1_SUPPORT_ROLE)
      ) as AvailFunc : (({ location }: any) =>
        !location?.pathname?.startsWith(MSP_PATH_PREFIX)
      ) as AvailFunc
    )
    mergeSettings(MSP_SETTINGS_KEY, hasMicroApp(ASC_PORTAL_MICRO_APP) ?
      (({ access, location }: any) =>
        location?.pathname?.startsWith(MSP_PATH_PREFIX) &&
        !access?.hasRoleOnly(ASC_TIER_1_SUPPORT_ROLE)
      ) as AvailFunc : (({ location }: any) =>
        location?.pathname?.startsWith(MSP_PATH_PREFIX)
      ) as AvailFunc
    )
    // _.remove(actions, item => item.asPlaceholder) // will remove before render
  }
  if (actions?.length) {
    values.leftMainActions = actions
    const hasRoute = (action: NavAction) => action.key && action.path && action.component
    const navActionRoutes: NavAction[] = []
    for (const action of actions) {
      if (hasRoute(action)) {
        navActionRoutes.push(action)
      }
      else if (!action.component && isNonemptyArray(action.children)) {
        const routes = (action.children as NavAction[]).filter(hasRoute)
        if (routes.length) {
          navActionRoutes.push(...routes)
        }
      }
    }
    if (navActionRoutes.length) {
      values.navRoutes = leftMainNavigation.concat(navActionRoutes as NavItem[])
    }
  }

  const routes = fxVars.extensibility ? getRouteExtensions() : undefined
  if (routes?.length) {
    const stateLoadingPromiseCache = {} as Record<string, Promise<any> | undefined>
    const getStateLoadingPromises = (ext: { stateLoaders?: string[], microAppId: string }) => {
      if (isNonemptyArray(ext.stateLoaders)) {
        const stateLoaders = uniq(ext.stateLoaders).sort()
        const promiseMap = {} as Record<string, Promise<any>>
        const cacheKey = stateLoaders.join('|')
        if (stateLoadingPromiseCache[cacheKey]) {
          return stateLoadingPromiseCache[cacheKey]
        }
        for (const name of stateLoaders) {
          if (['auth', 'main'].includes(name)) {
            continue // ignore those for now
          }
          const promise = stateLoadingPromises[name]
          if (promise) {
            promiseMap[name] = promise
          }
          else if (!values[name]) {
            console.error(`cannot find state loader '${name}' for microapp '${ext.microAppId}'`)
          }
        }
        const promises = Object.values(promiseMap)
        // console.error('>>>stateLoaders', stateLoaders, promises, stateLoadingPromises)
        if (promises.length) {
          const promise = Promise.all(promises)
          promise.then(() => {
            Object.defineProperty(promise, 'resolved', { value: true })
          })
          stateLoadingPromiseCache[cacheKey] = promise
          return promise
        }
      }
    }
    values.routeExtensions = routes
    const leftMainRoutes = [] as NavItem[]
    for (const route of routes) {
      const { path, flags, isAllowed, replacePaths, fallbackRedirect, component, ...restRoute } = route
      const notExactMatch = path.endsWith('/*')
      const routePath = notExactMatch ? path.slice(0, -2) : path
      const exact = notExactMatch ? false : flags?.exact
      const navItem: NavItem = {
        key: path,
        path: routePath,
        exact,
        component: autoWrapLazy(component, { lazy: flags?.lazy as boolean | undefined }),
        ...restRoute,
        microAppId: route.microAppId,
        appName: route.microAppName,
        appVars: getMicroAppVars(route.microAppId),
        ...flags,
        avail: isAllowed as SparkyAvailFunc | undefined,
        availFallbackRedirect: fallbackRedirect,
        title: undefined,
      }
      const loadingPromise = getStateLoadingPromises(route)
      if (loadingPromise) {
        const orgIsLoading = navItem.isLoading
        navItem.isLoading = orgIsLoading == null ? () => !('resolved' in loadingPromise) : () => {
          if (orgIsLoading === false || (isFunction(orgIsLoading) && !orgIsLoading())) {
            return false
          }
          return !('resolved' in loadingPromise)
        }
        navItem.loadingPromise = loadingPromise
      }
      leftMainRoutes.push(navItem)
      if (replacePaths) {
        for (const replacePath of ensureArray(replacePaths)) {
          const isReplacePathNested = NESTED_PATH_REGEX.test(replacePath)
          const keyPath = isReplacePathNested ? replacePath.replace(NESTED_PATH_REGEX, '') : replacePath
          const redirectConfig = {
            key: `${keyPath}~>${routePath}`,
            path: keyPath,
            exact: navItem.exact,
            redirectTo: routePath,
            redirectWithSearch: true,
            title: undefined,
          } as NavItem
          const sameErrMsg = `ReplacePath '${keyPath}' cannot have the same path '${routePath}'. Ignore this redirection.`
          if (isReplacePathNested && NESTED_PATH_REGEX.test(path)) {
            if (exact) {
              console.error(`ReplacePath '${path}' cannot have both 'exact' and nested path '${replacePath}' set. Ignore this redirection.`)
              continue
            }
            const replacePathWithSlash = replacePath.replace(NESTED_PATH_REGEX, '/')
            const replacePathWithoutSlash = replacePathWithSlash.endsWith('/') ?
              replacePathWithSlash.slice(0, -1) : replacePathWithSlash
            const routePathWithSlash = path.replace(NESTED_PATH_REGEX, '/')
            const routePathWithoutSlash = routePathWithSlash.endsWith('/') ?
              routePathWithSlash.slice(0, -1) : routePathWithSlash
            if (routePathWithSlash === replacePathWithSlash ||
              replacePathWithoutSlash === routePathWithoutSlash) {
              console.error(sameErrMsg)
              continue
            }
            Object.assign(redirectConfig, {
              path: replacePathWithoutSlash,
              exact: false,
              redirectTo: (params) => {
                const pathname = (params || window).location.pathname
                const redirectToPath = pathname === replacePathWithoutSlash ? routePathWithoutSlash :
                  pathname.replace(replacePathWithSlash, routePathWithSlash)
                __info(`redirect with nested path from '${pathname}' to '${redirectToPath}'`)
                return redirectToPath
              },
            } as Partial<NavItem>)
          }
          if (replacePath === path || keyPath === routePath) {
            console.error(sameErrMsg)
            continue
          }
          leftMainRoutes.push(redirectConfig)
        }
      }
    }
    const ffUseNav3Val = ffUseNav3()
    const redirects = ffUseNav3Val ? getExtensions(SPARKY_NAV3_REDIRECTS_EXTPOINT) : undefined
    if (redirects?.length) {
      for (const redirect of redirects) {
        const { path, redirectTo, push, exact, preserveSearch } = redirect
        const exactMatch = !path.endsWith('/*')
        const redirectWithNestedPaths = isString(redirectTo) && NESTED_PATH_REGEX.test(redirectTo)
        const routePath = redirectWithNestedPaths ? path.replace(NESTED_PATH_REGEX, '/') : exactMatch ? path : path.slice(0, -2)
        const redirectPath = redirectWithNestedPaths ? redirectTo.replace(NESTED_PATH_REGEX, '/') : redirectTo
        if (redirectWithNestedPaths && exact) {
          console.error(`Redirect route '${path}' cannot have both 'exact' and nested path '${redirectTo}' set. Ignore this redirection.`)
          continue
        }
        if (redirectTo === path || redirectPath === routePath) {
          console.error(`Redirect route '${path}' cannot have the same path as the redirectTo '${redirectTo}'. Ignore this redirection.`)
          continue
        }
        leftMainRoutes.push({
          key: `${routePath}~>${isString(redirectTo) ? redirectTo : `fn(${(redirect as { extensionId?: string }).extensionId || ''})`}`,
          path: routePath,
          exact: redirectWithNestedPaths ? false : (exact ?? exactMatch),
          push,
          redirectTo: redirectWithNestedPaths ? (params) => {
            const pathname = (params || window).location.pathname
            const redirectToPath = pathname.replace(routePath, redirectPath as string)
            __info(`redirect with nested path from '${pathname}' to '${redirectToPath}'`)
            return redirectToPath
          } : redirectTo,
          redirectWithSearch: preserveSearch,
          title: undefined,
        } as NavItem)
      }
    }
    values.leftMainRoutes = leftMainRoutes
  }
  return values
}
